import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Dimmer, Loader } from "semantic-ui-react";
import {
  Process,
  ProcessFormValues,
  useCreateOriginalRecipeProcess,
  useCreateVariantProcess,
  useDeleteRecipeProcess,
  useGetAvailableProcesses
} from "../../../api/recipeSimulator";
import { useGetCountries } from "../../../api/static-data";

import { Plus } from "lucide-react";
import { CustomButton } from "../../../components/ui/button/CustomButton/CustomButton";
import { Button } from "../../../components/ui/common/button";
import { Form } from "../../../components/ui/common/form";
import { ScrollArea } from "../../../components/ui/common/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "../../../components/ui/common/sheet";
import {
  DEFAULT_COUNTRY_CODE,
  DENMARK_COUNTRY_CODE,
  noResultIngredient
} from "../../../config/constants";
import {
  IngredientDataTypesOption,
  ProcessDirectionOption,
  TemperatureUnitOptions
} from "../../../config/enums/IngredientType.enum";
import { MEASUREMENT_TYPES } from "../../../config/enums/MeasurementTypes";
import { RecipeType, RecipeTypeClassification } from "../../../config/enums/recipeSimulator.enum";
import { errorView } from "../../../helpers/ErrorHandler";
import {
  getExistingProcessesInContext,
  isProcessNameUnique
} from "../../../helpers/ProcessValidationHelper";
import {
  buildRecipeProcessPayload,
  normalizeProcessForForm
} from "../../../helpers/ProcessPayloadHelper";
import "./RecipeAddProcessingStepModal.scss";
import ProcessorCard from "./component/ProcessorCard";

// Form values with UI-only state and nullable semi-specific fields
type ProcessFormData = Omit<
  ProcessFormValues,
  | "water"
  | "carbohydrates"
  | "protein"
  | "fat"
  | "ashMinerals"
  | "starch"
  | "hydrocolloidsGums"
  | "fiber"
  | "gellingProtein"
  | "gelatin"
  | "solidFat"
  | "startingTemperature"
  | "targetTemperature"
  | "maintenanceTime"
> & {
  isCollapsed?: boolean;
  saveStatue?: boolean;
  isNewProcess?: boolean;
  isUpdated?: boolean;
  water?: number | null;
  carbohydrates?: number | null;
  protein?: number | null;
  fat?: number | null;
  ashMinerals?: number | null;
  starch?: number | null;
  hydrocolloidsGums?: number | null;
  fiber?: number | null;
  gellingProtein?: number | null;
  gelatin?: number | null;
  solidFat?: number | null;
  startingTemperature?: number | null;
  targetTemperature?: number | null;
  maintenanceTime?: number | null;
  targetTemperatureUnit?: string | null;
  startingTemperatureUnit?: string | null;
  processDirection?: string;
};

interface RecipeAddProcessingStepModalProps {
  visibleModal: boolean;
  setVisibleModal: (visible: boolean) => void;
  recipeDataRefetch?: () => void;
  variantId?: string | null;
  setVariantId?: (id: string | null) => void;
  genericProcess?: any;
  recipeData?: any;
  isOriginal?: boolean;
  usedColors?: string[];
}

const RecipeAddProcessingStepModal = ({
  visibleModal,
  setVisibleModal,
  recipeDataRefetch,
  variantId,
  setVariantId,
  genericProcess,
  recipeData,
  isOriginal,
  usedColors = []
}: RecipeAddProcessingStepModalProps) => {
  // Router params
  const { recipeId } = useParams();

  // Component state
  const [defaultProcessName, setDefaultProcessName] = useState<string>("");
  const [processType, setProcessType] = useState<string | null>(null);
  const previousVariantRef = useRef<string | null | undefined>(undefined);
  const shouldRefreshRef = useRef<boolean>(false);

  // API queries
  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  const { data: availableProcessesData, isFetching: isAvailableProcessesLoading } =
    useGetAvailableProcesses(recipeId ?? "", variantId ?? "");

  // Memoized values
  const countries = useMemo(() => {
    if (isCountriesDataSuccess) {
      const getCountryData = countriesData?.map((c: any) => ({
        key: c.id,
        text: c.country,
        value: c.id,
        alpha_code: c.alpha_code
      }));
      return getCountryData;
    }
  }, [countriesData, isCountriesDataSuccess]);

  const getDefaultCountry = useCallback(() => {
    const denmarkValue = countries?.find((e: any) => e.alpha_code === DEFAULT_COUNTRY_CODE);
    return denmarkValue?.key?.toString() || "47";
  }, [countries]);

  const defaultTemperatureUnit = TemperatureUnitOptions.CELSIUS;

  const currentAvailableProcesses = useMemo(() => {
    if (!recipeData) return [];

    const existingVariant = isOriginal
      ? recipeData.originalRecipe || []
      : recipeData.variants?.find((p: any) => p._id === variantId) || [];

    return (
      existingVariant?.processes?.map(
        (process: any) =>
          ({
            ...normalizeProcessForForm(process, {
              defaultCountry: getDefaultCountry() || "47",
              defaultTemperatureUnit
            }),
            isCollapsed: true,
            saveStatue: true,
            isNewProcess: false
          } as ProcessFormData)
      ) || []
    );
  }, [variantId, recipeData, getDefaultCountry, isOriginal]);

  const availableProcesses = useMemo(() => {
    if (!availableProcessesData) return [];

    return [
      { id: 0, title: "+ New" },
      ...availableProcessesData.map(p => ({
        id: p._id,
        title: p.title,
        processData: p
      }))
    ];
  }, [availableProcessesData]);

  // Form setup
  const form = useForm<{ processes: ProcessFormData[] }>({
    mode: "all",
    defaultValues: {
      processes: availableProcesses.length > 0 ? currentAvailableProcesses : []
    }
  });

  const {
    reset,
    control,
    trigger,
    getValues,
    formState: { errors }
  } = form;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "processes"
  });

  // Modal close handler
  const modalClose = useCallback(() => {
    setVisibleModal(false);
    recipeDataRefetch?.();
    setVariantId?.(null);
    previousVariantRef.current = undefined;
  }, [setVisibleModal, recipeDataRefetch, setVariantId]);

  const initialProcessData: ProcessFormData = {
    processName: "",
    input: null,
    output: null,
    country: getDefaultCountry() || DENMARK_COUNTRY_CODE,
    genericProcessId: null,
    sideStream: null,
    electricity: null,
    heat: null,
    wastewater: null,
    processNote: "",
    dataType: IngredientDataTypesOption.SPECIFIC,
    isCollapsed: false,
    inputUnit: MEASUREMENT_TYPES.WEIGHT_KG,
    outputUnit: MEASUREMENT_TYPES.WEIGHT_KG,
    sideStreamUnit: MEASUREMENT_TYPES.WEIGHT_KG,
    electricityUnit: MEASUREMENT_TYPES.POWER_kWh_KG,
    wastewaterUnit: MEASUREMENT_TYPES.VOLUME_m3_PER_KG,
    heatUnit: MEASUREMENT_TYPES.ENERGY_MJ_KG,
    water: null,
    carbohydrates: null,
    protein: null,
    fat: null,
    ashMinerals: null,
    starch: null,
    hydrocolloidsGums: null,
    fiber: null,
    gellingProtein: null,
    gelatin: null,
    solidFat: null,
    startingTemperature: null,
    targetTemperature: null,
    maintenanceTime: null,
    targetTemperatureUnit: defaultTemperatureUnit,
    startingTemperatureUnit: defaultTemperatureUnit,
    processDirection: ProcessDirectionOption.HEATING,
    coolingMethod: null,
    durationValue: undefined,
    durationType: "HOURS",
    saveStatue: false,
    isNewProcess: false,
    isUpdated: true,
    // Process relationship tracking defaults
    originalRecipeId: undefined,
    originalProcessId: undefined,
    recipeTypeClassification: RecipeTypeClassification.PRIVATE
  };

  // Success handler
  const handleCreateProcessSuccess = useCallback(() => {
    recipeDataRefetch?.();
    shouldRefreshRef.current = true;
  }, [recipeDataRefetch, modalClose, reset, getDefaultCountry]);

  // API mutations
  const { mutate: createOriginalProcess, isLoading } = useCreateOriginalRecipeProcess(
    recipeId ?? "",
    {
      onSuccess: () => {
        handleCreateProcessSuccess();
      }
    }
  );

  const { mutate: createVariantProcess, isLoading: isCreatingVariantProcess } =
    useCreateVariantProcess(recipeId ?? "", variantId ?? "", {
      onSuccess: () => {
        handleCreateProcessSuccess();
      }
    });

  const { mutate: deleteProcess, isLoading: isProcessDeleting } = useDeleteRecipeProcess();

  // Effects
  useEffect(() => {
    if (visibleModal && previousVariantRef.current !== variantId) {
      const initialProcesses =
        currentAvailableProcesses.length > 0 ? currentAvailableProcesses : [];
      reset({ processes: initialProcesses });
      previousVariantRef.current = variantId;
    }
  }, [visibleModal, variantId, currentAvailableProcesses, reset]);

  useEffect(() => {
    if (shouldRefreshRef.current && currentAvailableProcesses.length > 0) {
      const updatedProcesses = currentAvailableProcesses.map((p: any) => ({
        ...p,
        isCollapsed: true
      }));
      reset({ processes: updatedProcesses });
      shouldRefreshRef.current = false;
    }
  }, [currentAvailableProcesses, reset]);

  // Form submission handler
  const onSubmitSingleProcess = useCallback(
    async (index: number) => {
      const isValid = await trigger(`processes.${index}`);
      if (!isValid) return;

      const allProcesses = getValues("processes");
      const process = allProcesses[index];
      if (!process) return;

      if (!process.processName) {
        errorView("Processing name is required");
        return;
      }

      if (!process.country && process.dataType !== IngredientDataTypesOption.GENERIC) {
        errorView("Processing country is required");
        return;
      }

      // Validate process name uniqueness
      const existingProcesses = getExistingProcessesInContext(
        recipeData,
        isOriginal || !variantId,
        variantId
      );

      if (!isProcessNameUnique(process.processName, existingProcesses)) {
        errorView("Process already exists");
        return;
      }

      if (process.saveStatue) return;

      const { isCollapsed, ...processData } = process;

      const processPayload = buildRecipeProcessPayload(processData, {
        defaultTemperatureUnit,
        variantId,
        usedColors,
        fallbackGenericProcessId: processType
      });

      if (variantId) {
        createVariantProcess({
          recipeId: recipeId ?? "",
          variantId: variantId ?? "",
          processes: [processPayload as Process]
        });
      } else {
        createOriginalProcess({
          recipeId: recipeId ?? "",
          variantId: "",
          processes: [processPayload as Process]
        });
      }
    },
    [
      trigger,
      getValues,
      processType,
      variantId,
      recipeId,
      createVariantProcess,
      createOriginalProcess,
      usedColors
    ]
  );

  // Search handler
  const getSearchValue = useCallback(
    (index: number) => (data: any) => {
      const { result } = data;

      if (result?.id === 0) {
        update(index, {
          ...initialProcessData,
          isNewProcess: true
        });
        setDefaultProcessName("");
      } else {
        const processData = result?.processData || result?.other?.processData;
        if (processData) {
          const { _id, ...processDataWithoutId } = processData;
          update(index, {
            ...processDataWithoutId,
            country: processData.country?.toString() || getDefaultCountry() || "47",
            isCollapsed: false,
            saveStatue: false,
            isNewProcess: false,
            genericProcessId: processData.genericProcessId?.toString() || null,
            // Capture source process relationship for duplication tracking
            originalRecipeId: recipeId,
            originalProcessId: processData._id,
            recipeTypeClassification: RecipeTypeClassification.PRIVATE
          });
          setDefaultProcessName(processData.processName);
          setProcessType(processData.genericProcessId ?? null);
        }
      }
    },
    [update, getDefaultCountry, recipeId]
  );

  // UI helpers
  const customResultTextView = useCallback(
    (title: string, id: number) => (
      <p
        className={
          id === noResultIngredient.id && title === noResultIngredient.title
            ? "customSearchResultText"
            : "searchResultText"
        }
      >
        {title}
      </p>
    ),
    []
  );

  const updateProcessor = useCallback(
    (index: number, updated: any) => {
      update(index, updated);
    },
    [update]
  );

  const handleDeleteProcess = useCallback(
    (index: number) => {
      const process = fields[index];
      if (!process?._id) {
        remove(index);
        return;
      }
      deleteProcess(
        {
          recipeId: recipeId ?? "",
          processId: process._id,
          variantId: variantId ?? "",
          recipeType: isOriginal ? RecipeType.ORIGINAL : RecipeType.VARIANT
        },
        {
          onSuccess: () => {
            remove(index);
            recipeDataRefetch?.();
          }
        }
      );
    },
    [fields, deleteProcess, recipeId, variantId, isOriginal, remove, recipeDataRefetch]
  );

  const removeProcessor = useCallback(
    (index: number) => {
      handleDeleteProcess(index);
    },
    [handleDeleteProcess]
  );

  const addProcessor = useCallback(() => {
    append({
      ...initialProcessData
    });
  }, [append, getDefaultCountry]);

  const enableNewProcess = useMemo(() => fields.some((field: any) => !field.saveStatue), [fields]);

  // Loading state
  const isLoaderVisible =
    isLoading || isCountriesDataLoading || isAvailableProcessesLoading || isCreatingVariantProcess;

  if (isLoaderVisible) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  // Render empty state
  const renderEmptyState = () => (
    <div className="tw-text-center tw-py-12 tw-space-y-3">
      <p className="tw-text-sm tw-text-muted-foreground">No process added yet.</p>
      <Button onClick={addProcessor} size="lg" className="tw-gap-1.5">
        <Plus className="tw-h-3.5 tw-w-3.5" />
        Add First Process
      </Button>
    </div>
  );

  // Render process list
  const renderProcessList = () => (
    <>
      {fields.map((field: any, idx: number) => (
        <ProcessorCard
          key={field.id}
          processor={field}
          index={idx}
          onChange={updated => updateProcessor(idx, updated)}
          onRemove={() => removeProcessor(idx)}
          onSubmitSingle={() => onSubmitSingleProcess(idx)}
          variantId={variantId}
          availableProcessesData={availableProcessesData}
          defaultProcessName={defaultProcessName}
          control={control}
          setValue={form.setValue}
          countries={countries}
          getSearchValue={getSearchValue(idx)}
          customResultTextView={customResultTextView}
          availableProcesses={availableProcesses}
          genericProcess={genericProcess}
        />
      ))}
      {!enableNewProcess && (
        <div className="tw-flex tw-justify-center tw-pt-2.5">
          <CustomButton
            onClick={addProcessor}
            icon
            iconChild={<Plus size={20} />}
            iconWithText={!!Plus}
            title="Add Process"
            theme="green"
          />
        </div>
      )}
    </>
  );

  return (
    <Sheet open={visibleModal} onOpenChange={setVisibleModal}>
      <SheetContent side="right" className="tw-w-full sm:tw-max-w-6xl !tw-p-0 tw-flex tw-flex-col">
        <SheetHeader className="tw-px-6 tw-pt-1 tw-pb-1 tw-border-b tw-border-border tw-shrink-0">
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-py-4">
            <div className="tw-flex-1">
              <SheetTitle className="tw-font-display tw-text-xl">Process Configuration</SheetTitle>
              <SheetDescription className="tw-mt-1 tw-text-sm">
                Add and configure processing steps for this variation.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <Form {...form}>
          <ScrollArea className={`tw-flex-1 tw-px-6 tw-py-4`}>
            <div className="tw-space-y-4 tw-pb-4">
              {fields.length === 0 ? renderEmptyState() : renderProcessList()}
            </div>
          </ScrollArea>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

export default RecipeAddProcessingStepModal;
